'use client';

import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import { useTranslations } from 'next-intl';

/** 商品信息类型（仅提取图片所需字段，可按需扩展） */
interface ProductInfo {
  image?: {
    product?: string[];
  };
}

/** 弹窗原始数据结构 */
export interface IShowCannotSubmit {
  show: boolean;
  /** 8: 替换赠品; 9: 两款赠品均无库存 */
  code: 8 | 9;
  old_product?: ProductInfo;
  new_product?: ProductInfo;
}

export interface MemberShipGiftInvalidProps {
  data: IShowCannotSubmit;
  /** 关闭 / Decline */
  onClose: () => void;
  /** Proceed to checkout */
  onContinue: () => void;
}

/** 获取商品主图 */
const getProdImage = (productInfo?: ProductInfo) => {
  return productInfo?.image?.product?.[0] ?? '';
};

export default function MemberShipGiftInvalid({
  data,
  onClose,
  onContinue,
}: MemberShipGiftInvalidProps) {
  const t = useTranslations('payment.Popup');
  const { code } = data;

  const handleDecline = () => {
    onClose();
  };

  const handleProceed = () => {
    onContinue();
  };

  return (
    <Dialog
      open={data.show}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      footer={null}
      showCloseIcon
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '300px', sm: '32vw', md: '31.25vw' },
          maxWidth: '100%',
          borderRadius: { xs: '10px', sm: '8px' },
          padding: { xs: '25px 0 21px', sm: '3.23vw 2.08vw 1.82vw' },
        },
      }}
    >
      <div className="flex flex-col items-center w-full">
        {code === 8 && (
          <>
            <p className="text-[20px] font-bb text-primary-black">
              {t('MembershipGiftOutOfStock')}
            </p>
            {/* 图片列表 */}
            <ul className="w-full max-h-[62vh] overflow-y-auto flex flex-col items-center mt-[16px]">
              <li className="flex flex-col items-center py-[1.8vw]">
                <div className="relative overflow-hidden">
                  <Image
                    src={getProdImage(data.old_product)}
                    alt="old-product"
                    width={200}
                    height={200}
                    className="object-contain w-[15.63vw] h-auto max-w-[200px]"
                  />
                  {/* Sold out overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                    <span className="flex items-center justify-center w-[5.33vw] h-[5.33vw] max-w-[80px] max-h-[80px] rounded-full bg-black/70 text-white text-[12px]">
                      {t('OutOfStock')}
                    </span>
                  </div>
                </div>
              </li>
              <li className="py-0 text-[16px]">{t('ReplaceWith')}</li>
              <li className="flex flex-col items-center py-[1.8vw]">
                <div className="overflow-hidden">
                  <Image
                    src={getProdImage(data.new_product)}
                    alt="new-product"
                    width={200}
                    height={200}
                    className="object-contain w-[15.63vw] h-auto max-w-[200px]"
                  />
                </div>
              </li>
            </ul>
          </>
        )}

        {code === 9 && (
          <p className="text-[16px] font-bb text-center leading-[100px] h-[120px]">
            {t('MembershipGiftOutOfStock')}
          </p>
        )}

        {/* 底部按钮 */}
        <div className="flex justify-between items-center w-full mt-[10px] space-x-[20px]">
          <Button
            variant="outlined"
            className="flex-1 h-[40px] font-bb"
            sx={{ borderColor: 'primaryPurple.main', color: 'primaryPurple.main' }}
            onClick={handleDecline}
          >
            {t('Decline')}
          </Button>
          <Button variant="contained" className="flex-1 h-[40px] font-bb" onClick={handleProceed}>
            {t('ProceedToCheckout')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
